import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface User {
  id: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin: string | null;
}

interface Role {
  id: string;
  name: string;
  description: string;
}

interface CreateUserData {
  email: string;
  password: string;
  roleId: string;
}

interface UpdateUserData {
  email: string;
  roleId: string;
  status: 'active' | 'inactive';
  password?: string; // 可选的密码字段
}

export const Users: React.FC = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    password: '',
    roleId: ''
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false); // 控制密码重置字段显示
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

  // 🔒 严格的管理员权限检查
  const isAdmin = currentUser?.role?.name === 'Administrator';
  
  // 🔒 防止非管理员访问此页面
  useEffect(() => {
    if (!isAdmin && !loading) {
      showSnackbar('权限不足：只有管理员可以管理用户', 'error');
    }
  }, [isAdmin, loading]);

  // API调用辅助函数
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 加载用户列表
  const loadUsers = async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiCall('/api/users');
      console.log('Users API 原始响应:', data); // 调试日志
      
      // 🔧 修复：处理多种数据格式，确保始终返回数组
      let usersArray: any[] = [];
      
      if (data.data?.users && Array.isArray(data.data.users)) {
        // 从D1数据库获取的数据格式
        usersArray = data.data.users;
      } else if (Array.isArray(data.data)) {
        // 直接数组格式
        usersArray = data.data;
      } else if (Array.isArray(data)) {
        // 顶级数组格式
        usersArray = data;
      } else {
        console.error('未知的用户数据格式:', data);
        usersArray = [];
      }
      
      console.log('Users - 解析后的用户数组:', usersArray); // 调试日志
      
      // 🔧 确保数组有效且不为null/undefined
      if (!Array.isArray(usersArray)) {
        console.warn('用户数据不是数组，重置为空数组:', usersArray);
        usersArray = [];
      }
      
      // 🔧 数据结构映射修复：后端字段 -> 前端期望格式
      const mappedUsers: User[] = usersArray.map((user: any) => ({
        id: user.id || '',
        email: user.email || '',
        roleId: user.roleId || user.role_id || '',
        roleName: user.roleName || user.role_name || '',
        status: (user.status === 'active' || user.is_active === 1 || user.is_active === true || user.is_active === 'active' ? 'active' : 'inactive') as 'active' | 'inactive',
        createdAt: user.createdAt || user.created_at || '',
        lastLogin: user.lastLogin || user.last_login || null
      }));
      
      console.log('Users - 映射后的用户数据:', mappedUsers); // 调试日志
      
      // 🔧 最终安全检查
      if (Array.isArray(mappedUsers)) {
        setUsers(mappedUsers);
      } else {
        console.error('映射后的用户数据不是数组，设置空数组');
        setUsers([]);
      }
    } catch (error) {
      console.error('加载用户失败:', error);
      showSnackbar('加载用户列表失败', 'error');
      setUsers([]); // 设置空数组防止map错误
    } finally {
      setLoading(false);
    }
  };

  // 加载角色列表
  const loadRoles = async () => {
    if (!isAdmin) return;

    try {
      const data = await apiCall('/api/roles');
      console.log('Roles API 原始响应:', data); // 调试日志
      
      // 🔧 修复：处理多种数据格式，确保始终返回数组
      let rolesArray: any[] = [];
      
      if (data.data?.roles && Array.isArray(data.data.roles)) {
        rolesArray = data.data.roles;
      } else if (Array.isArray(data.data)) {
        rolesArray = data.data;
      } else if (Array.isArray(data)) {
        rolesArray = data;
      } else {
        console.error('未知的角色数据格式:', data);
        rolesArray = [];
      }
      
      console.log('Roles - 解析后的角色数组:', rolesArray); // 调试日志
      
      // 🔧 确保数组有效且不为null/undefined
      if (!Array.isArray(rolesArray)) {
        console.warn('角色数据不是数组，重置为空数组:', rolesArray);
        rolesArray = [];
      }
      
      // 🔧 权限过滤：非超级管理员不能看到管理员角色
      let availableRoles = rolesArray;
      if (currentUser?.role?.name !== 'Administrator') {
        availableRoles = rolesArray.filter((role: any) => role.name !== 'Administrator');
      }
      
      console.log('Roles - 过滤后的角色数据:', availableRoles); // 调试日志
      
      // 🔧 最终安全检查
      if (Array.isArray(availableRoles)) {
        setRoles(availableRoles);
      } else {
        console.error('过滤后的角色数据不是数组，设置空数组');
        setRoles([]);
      }
    } catch (error) {
      console.error('加载角色失败:', error);
      showSnackbar('加载角色列表失败', 'error');
      setRoles([]); // 设置空数组防止map错误
    }
  };

  // 创建用户
  const createUser = async () => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以创建用户', 'error');
      return;
    }

    // 🔒 检查是否尝试创建管理员账号
    const selectedRole = Array.isArray(roles) ? roles.find(r => r.id === formData.roleId) : null;
    if (selectedRole?.name === 'Administrator' && currentUser?.role?.name !== 'Administrator') {
      showSnackbar('权限不足：无法创建管理员账号', 'error');
      return;
    }

    try {
      const data = await apiCall('/api/users', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      
      showSnackbar('用户创建成功', 'success');
      setOpenDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('创建用户失败:', error);
      showSnackbar((error as Error).message || '创建用户失败', 'error');
    }
  };

  // 更新用户
  const updateUser = async () => {
    if (!isAdmin || !editingUser) {
      showSnackbar('权限不足：只有管理员可以更新用户', 'error');
      return;
    }

    // 🔒 检查是否尝试分配管理员权限
    const selectedRole = Array.isArray(roles) ? roles.find(r => r.id === formData.roleId) : null;
    if (selectedRole?.name === 'Administrator' && currentUser?.role?.name !== 'Administrator') {
      showSnackbar('权限不足：无法分配管理员权限', 'error');
      return;
    }

    try {
      const updateData: any = {
        email: formData.email,
        roleId: formData.roleId
      };
      
      // 🔧 如果设置了新密码，则包含密码字段
      if (showPasswordReset && formData.password.trim()) {
        updateData.password = formData.password;
      }
      
      await apiCall(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      showSnackbar('用户更新成功', 'success');
      setOpenDialog(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('更新用户失败:', error);
      showSnackbar((error as Error).message || '更新用户失败', 'error');
    }
  };

  // 删除用户
  const deleteUser = async (userId: string) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以删除用户', 'error');
      return;
    }

    // 🔒 防止删除自己
    if (userId === currentUser?.id) {
      showSnackbar('不能删除自己的账号', 'error');
      return;
    }

    if (!window.confirm('确定要删除这个用户吗？此操作不可撤销。')) {
      return;
    }

    try {
      await apiCall(`/api/users/${userId}`, {
        method: 'DELETE',
      });

      showSnackbar('用户删除成功', 'success');
      loadUsers();
    } catch (error) {
      console.error('删除用户失败:', error);
      showSnackbar((error as Error).message || '删除用户失败', 'error');
    }
  };

  // 切换用户状态
  const toggleUserStatus = async (userId: string, currentStatus: string) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以修改用户状态', 'error');
      return;
    }

    // 🔒 防止禁用自己
    if (userId === currentUser?.id) {
      showSnackbar('不能禁用自己的账号', 'error');
      return;
    }

    try {
      // 🔧 使用正确的API端点
      await apiCall(`/api/users/${userId}/toggle-status`, {
        method: 'PATCH'
      });

      const newStatus = currentStatus === 'active' ? '停用' : '启用';
      showSnackbar(`用户${newStatus}成功`, 'success');
      loadUsers();
    } catch (error) {
      console.error('切换用户状态失败:', error);
      showSnackbar((error as Error).message || '操作失败', 'error');
    }
  };

  // 显示通知
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      roleId: ''
    });
    setShowPasswordReset(false); // 重置密码重置状态
  };

  // 格式化日期
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '从未登录';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 处理创建
  const handleCreate = () => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以创建用户', 'error');
      return;
    }
    setEditingUser(null);
    resetForm();
    setOpenDialog(true);
  };

  // 处理编辑
  const handleEdit = (user: User) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以编辑用户', 'error');
      return;
    }
    
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // 密码不预填
      roleId: user.roleId
    });
    setShowPasswordReset(false); // 重置密码重置状态
    setOpenDialog(true);
    setAnchorEl(null);
  };

  // 处理表单提交
  const handleSubmit = () => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以执行此操作', 'error');
      return;
    }

    if (!formData.email || !formData.roleId) {
      showSnackbar('请填写邮箱和角色', 'error');
      return;
    }

    if (!editingUser && !formData.password) {
      showSnackbar('创建用户时密码不能为空', 'error');
      return;
    }
    
    // 🔧 编辑用户时，如果启用了密码重置但密码为空，提示错误
    if (editingUser && showPasswordReset && !formData.password.trim()) {
      showSnackbar('请输入新密码', 'error');
      return;
    }

    if (editingUser) {
      updateUser();
    } else {
      createUser();
    }
  };

  // 菜单操作
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理用户', 'error');
      return;
    }
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  // 获取角色颜色
  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'Administrator': return 'error';
      case 'User': return 'primary';
      case 'ReadOnly': return 'secondary';
      default: return 'default';
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  if (loading) {
    return <LoadingSpinner message="加载用户数据..." />;
  }

  // 🔒 如果不是管理员，显示权限不足页面
  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            权限不足
          </Typography>
          <Typography>
            只有系统管理员才能访问用户管理功能。如需管理用户权限，请联系系统管理员。
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          用户管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={!isAdmin}
        >
          添加用户
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>邮箱</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>启用/停用</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>最后登录</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 🔧 修复：确保 users 始终是数组 */}
            {Array.isArray(users) && users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.roleName}
                      color={getRoleColor(user.roleName)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status === 'active' ? '活跃' : '停用'}
                      color={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={user.status === 'active' ? '点击停用用户' : '点击启用用户'}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.status === 'active'}
                            onChange={() => toggleUserStatus(user.id, user.status)}
                            color="primary"
                            disabled={user.id === currentUser?.id} // 🔒 防止操作自己
                          />
                        }
                        label=""
                        sx={{ margin: 0 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuClick(e, user)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {loading ? '加载中...' : '暂无用户数据'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 操作菜单 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedUser && handleEdit(selectedUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>编辑</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedUser) {
              deleteUser(selectedUser.id);
            }
            handleMenuClose();
          }}
          disabled={selectedUser?.id === currentUser?.id} // 🔒 防止删除自己
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>删除</ListItemText>
        </MenuItem>
      </Menu>

      {/* 创建/编辑对话框 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? '编辑用户' : '创建用户'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="邮箱地址"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />

            {!editingUser ? (
              <TextField
                label="密码"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                required
                helperText="密码至少6位字符"
              />
            ) : (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPasswordReset}
                      onChange={(e) => setShowPasswordReset(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="重置密码"
                />
                {showPasswordReset && (
                  <TextField
                    label="新密码"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    fullWidth
                    required
                    helperText="输入新密码，至少6位字符"
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
            )}

            <FormControl fullWidth required>
              <InputLabel>用户角色</InputLabel>
              <Select
                value={formData.roleId}
                label="用户角色"
                onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
              >
                {/* 🔧 修复：确保 roles 始终是数组 */}
                {Array.isArray(roles) && roles.length > 0 ? (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {role.name}
                          {role.name === 'Administrator' && (
                            <Chip label="高级权限" color="error" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {role.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    <Typography color="text.secondary">
                      {loading ? '加载角色中...' : '暂无角色数据'}
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* 🔒 管理员权限警告 */}
            {Array.isArray(roles) && roles.find(r => r.id === formData.roleId)?.name === 'Administrator' && (
              <Alert severity="warning">
                <Typography variant="body2">
                  ⚠️ 您正在分配管理员权限。管理员拥有系统的完全控制权限，包括用户管理、角色管理和数据访问权限。
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? '更新' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 